import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, ArrowLeft, ChevronDown, ChevronUp, MessageCircle, Calendar, Users, Settings, Shield } from 'lucide-react';

const FAQ = () => {
  const [openSections, setOpenSections] = useState({
    gettingStarted: true,
    events: false,
    discord: false,
    troubleshooting: false,
    privacy: false
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const faqData = {
    gettingStarted: [
      {
        question: "How do I get started with the application?",
        answer: "To get started, you'll need a Discord account. Simply click the 'Login with Discord' button on the login page. This will authenticate you through Discord and create your account automatically. Once logged in, you can browse events, sign up for games, and manage your profile."
      },
      {
        question: "Do I need to create a separate account?",
        answer: "No! The application uses Discord OAuth for authentication, which means you don't need to create or remember a separate username and password. Your Discord account serves as your login credentials."
      },
      {
        question: "What information does the app collect from my Discord account?",
        answer: "The app only collects basic information from your Discord account: your username, display name, Discord user ID, and avatar. We do not access your email address, messages, or any other Discord data without your explicit permission."
      },
      {
        question: "Can I use the app without Discord?",
        answer: "Currently, Discord authentication is required to use the application. This ensures a secure and streamlined experience for our Blood on the Clocktower community."
      }
    ],
    events: [
      {
        question: "How do I sign up for an event?",
        answer: "To sign up for an event, navigate to the Events page and find the event you're interested in. You can either click 'View Details' to see more information and sign up, or use the quick signup buttons directly on the event card. Choose your status: Confirmed (green), Late (yellow), Tentative (orange), or Declined (red)."
      },
      {
        question: "What do the different signup statuses mean?",
        answer: "• Confirmed (Green): You're definitely coming to the event\n• Late (Yellow): You're coming but will arrive late\n• Tentative (Orange): You might come, but you're not sure yet\n• Declined (Red): You cannot attend the event"
      },
      {
        question: "Can I change my signup status after signing up?",
        answer: "Yes! You can update your signup status at any time. Go to the event details page and click 'Update My Signup' or use the edit button next to your name in the signups list. You can also add or modify notes about your attendance."
      },
      {
        question: "How do I create an event?",
        answer: "To create an event, you need to have 'creator' or 'admin' permissions. If you have these permissions, you'll see a 'Create Event' button on the Events page. Click it to fill out the event details including title, date, time, location, duration, and description."
      },
      {
        question: "Can I edit or delete an event I created?",
        answer: "Yes, if you're the event creator or an admin, you can edit or delete events. On the event details page, you'll see 'Edit' and 'Delete' buttons. Be careful with deletion as it cannot be undone."
      }
    ],
    discord: [
      {
        question: "How does Discord integration work?",
        answer: "The Discord integration uses OAuth 2.0 for secure authentication. When you log in, you're redirected to Discord's authorization page where you can approve the application to access basic profile information. The app then creates a secure session and can display your Discord avatar and username."
      },
      {
        question: "Does the app post to Discord automatically?",
        answer: "No, the app does not automatically post to Discord channels. It only uses Discord for user authentication and profile information. Any Discord notifications or announcements would need to be manually posted by event organizers."
      },
      {
        question: "Can I see other users' Discord information?",
        answer: "You can see other users' Discord usernames and avatars when they sign up for events, but this is limited to what they've made public on Discord. The app respects Discord's privacy settings."
      },
      {
        question: "What happens if I change my Discord username?",
        answer: "If you change your Discord username, the next time you log into the application, your profile will be updated with your new username. Your account and signup history will remain intact."
      },
      {
        question: "Is my Discord account secure?",
        answer: "Yes! The application uses Discord's official OAuth 2.0 system, which is the same secure authentication method used by many popular applications. We never store your Discord password and only access the information you explicitly authorize."
      }
    ],
    troubleshooting: [
      {
        question: "I can't log in with Discord. What should I do?",
        answer: "First, make sure you're logged into Discord in your browser. If you're still having issues, try clearing your browser cache and cookies, then try again. If the problem persists, contact the application administrators through the Discord server."
      },
      {
        question: "The app is loading slowly or not responding. What can I do?",
        answer: "Try refreshing the page. If the issue continues, check your internet connection and try again later. The application may be experiencing high traffic or maintenance. You can also try clearing your browser cache."
      },
      {
        question: "I can't see events that I know exist. Why?",
        answer: "Events may be filtered by status (active, completed, cancelled). Check the filter options on the Events page. Also, make sure you're looking at the correct time period - events are sorted by date."
      },
      {
        question: "My signup didn't save. What happened?",
        answer: "Check your internet connection and try signing up again. If you're still having issues, try refreshing the page and signing up from the event details page instead of using the quick signup buttons."
      },
      {
        question: "I can't edit an event I created. Why?",
        answer: "Make sure you're logged in with the same Discord account that created the event. If you're still having issues, contact an administrator as there may be a permissions issue."
      }
    ],
    privacy: [
             {
         question: "What data does the app store about me?",
         answer: "The app stores your Discord username, display name, Discord user ID, avatar, event signups, and any notes you add to your signups. We also store basic usage data to improve the application. Importantly, we do not directly capture personal information - any PII stored is voluntarily provided by you through your own actions."
       },
      {
        question: "Can I delete my account and data?",
        answer: "Yes, you can request deletion of your account and associated data. Contact the application administrators through the Discord server to make this request. Note that some event data may be retained for historical purposes."
      },
      {
        question: "Who can see my signup information?",
        answer: "Your signup status and notes are visible to event organizers and other participants in the same events. Your basic profile information (username, avatar) is visible to all users of the application."
      },
      {
        question: "Does the app use cookies?",
        answer: "No, the application does not use traditional cookies. Instead, it uses secure session management and local storage for user preferences. See our Cookie Policy for more details."
      },
             {
         question: "Where is my data stored?",
         answer: "All data is stored in the United States through our hosting providers: Firebase (front-end), Neon (database), and Render (backend server). This applies regardless of where you access the application from."
       },
       {
         question: "Is my data encrypted?",
         answer: "Yes, all data transmitted between your browser and our servers is encrypted using HTTPS. Data stored on our servers is also encrypted at rest for additional security. All data is stored in the United States through our hosting providers (Firebase, Neon, and Render)."
       }
    ]
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <Link
          to="/events"
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Events
        </Link>
      </div>

      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <HelpCircle className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Frequently Asked Questions</h1>
        </div>

        <div className="space-y-6">
          {/* Getting Started Section */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <button
              onClick={() => toggleSection('gettingStarted')}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Getting Started</h2>
              </div>
              {openSections.gettingStarted ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {openSections.gettingStarted && (
              <div className="px-6 pb-4 space-y-4">
                {faqData.gettingStarted.map((faq, index) => (
                  <div key={index} className="border-l-4 border-blue-200 dark:border-blue-800 pl-4">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{faq.question}</h3>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{faq.answer}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Events Section */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <button
              onClick={() => toggleSection('events')}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Users className="w-5 h-5 text-green-600 dark:text-green-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Events & Signups</h2>
              </div>
              {openSections.events ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {openSections.events && (
              <div className="px-6 pb-4 space-y-4">
                {faqData.events.map((faq, index) => (
                  <div key={index} className="border-l-4 border-green-200 dark:border-green-800 pl-4">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{faq.question}</h3>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{faq.answer}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Discord Integration Section */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <button
              onClick={() => toggleSection('discord')}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <MessageCircle className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Discord Integration</h2>
              </div>
              {openSections.discord ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {openSections.discord && (
              <div className="px-6 pb-4 space-y-4">
                {faqData.discord.map((faq, index) => (
                  <div key={index} className="border-l-4 border-purple-200 dark:border-purple-800 pl-4">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{faq.question}</h3>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{faq.answer}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Troubleshooting Section */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <button
              onClick={() => toggleSection('troubleshooting')}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Settings className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Troubleshooting</h2>
              </div>
              {openSections.troubleshooting ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {openSections.troubleshooting && (
              <div className="px-6 pb-4 space-y-4">
                {faqData.troubleshooting.map((faq, index) => (
                  <div key={index} className="border-l-4 border-orange-200 dark:border-orange-800 pl-4">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{faq.question}</h3>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{faq.answer}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Privacy Section */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <button
              onClick={() => toggleSection('privacy')}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Shield className="w-5 h-5 text-red-600 dark:text-red-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Privacy & Security</h2>
              </div>
              {openSections.privacy ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {openSections.privacy && (
              <div className="px-6 pb-4 space-y-4">
                {faqData.privacy.map((faq, index) => (
                  <div key={index} className="border-l-4 border-red-200 dark:border-red-800 pl-4">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{faq.question}</h3>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{faq.answer}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Additional Resources */}
        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Additional Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link
              to="/privacy-policy"
              className="flex items-center space-x-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <div>
                <h3 className="font-medium text-gray-900 dark:text-gray-100">Privacy Policy</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Learn about how we protect your data</p>
              </div>
            </Link>
            <Link
              to="/terms-of-use"
              className="flex items-center space-x-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <MessageCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              <div>
                <h3 className="font-medium text-gray-900 dark:text-gray-100">Terms of Use</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Read our terms and conditions</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Contact Information */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Still have questions?</h3>
          <p className="text-blue-800 dark:text-blue-200 text-sm">
            If you couldn't find the answer you're looking for, please contact us through the Discord server 
            or reach out to the application administrators directly.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQ; 